import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { format } from 'date-fns';
import RealisticCertificatePreview from './RealisticCertificatePreview';
import CertificateHistory from './CertificateHistory';
import RealisticTemplateSelector from './RealisticTemplateSelector';
import RealisticCustomization from './RealisticCustomization';
import { generatePDF, generateImage } from '../utils/certificateDownload';

const CertificateGenerator = () => {
  const [certificateType, setCertificateType] = useState('sick-leave');
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [hospitalData, setHospitalData] = useState({ hospital: '', state: '' });
  const [customization, setCustomization] = useState({
    fontStyle: 'printed',
    penColor: 'black',
    mastheadColor: 'red',
    clinicName: '',
    clinicAddress: ''
  });
  const [formData, setFormData] = useState({
    patientName: '',
    age: '',
    gender: 'Male',
    diagnosis: 'Fever (Pyrexia)',
    customDiagnosis: '',
    leaveFrom: format(new Date(), 'yyyy-MM-dd'),
    leaveTo: format(new Date(), 'yyyy-MM-dd'),
    doctorName: '',
    hospitalName: '',
    registrationNumber: '',
    dateOfIssue: format(new Date(), 'yyyy-MM-dd'),
    notes: ''
  });

  const [savedCertificates, setSavedCertificates] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('certificates');
    if (saved) {
      setSavedCertificates(JSON.parse(saved));
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    // Auto-fill hospital name if hospital template is selected
    if (templateId === 'govt-hospital' || templateId === 'private-hospital' || templateId === 'private-clinic') {
      setFormData(prev => ({ ...prev, hospitalName: '' }));
    }
  };

  const handleCustomizationChange = (field, value) => {
    setCustomization(prev => ({ ...prev, [field]: value }));
  };

  const handleHospitalDataChange = (data) => {
    setHospitalData(data);
    setFormData(prev => ({ ...prev, hospitalName: data.hospital }));
  };

  const saveCertificate = () => {
    const certificate = {
      id: Date.now(),
      type: certificateType,
      template: selectedTemplate,
      hospitalData: hospitalData,
      customization: customization,
      data: formData,
      createdAt: new Date().toISOString()
    };
    const updated = [certificate, ...savedCertificates];
    setSavedCertificates(updated);
    localStorage.setItem('certificates', JSON.stringify(updated));
    alert('Certificate saved successfully!');
  };

  const downloadPDF = () => {
    generatePDF('certificate-preview', formData.patientName);
  };

  const downloadImage = () => {
    generateImage('certificate-preview', formData.patientName);
  };

  const deleteCertificate = (id) => {
    const updated = savedCertificates.filter(cert => cert.id !== id);
    setSavedCertificates(updated);
    localStorage.setItem('certificates', JSON.stringify(updated));
  };

  const clearAll = () => {
    if (window.confirm('Are you sure you want to clear all certificates?')) {
      setSavedCertificates([]);
      localStorage.removeItem('certificates');
    }
  };

  const diagnosisOptions = [
    'Fever (Pyrexia)',
    'Common Cold / URTI',
    'Influenza (Flu)',
    'Typhoid Fever',
    'Gastroenteritis',
    'Physical Injury',
    'Post-Surgical Recovery',
    'Migraine / Headache',
    'Back Pain',
    'Anxiety / Stress',
    'Dental Procedure',
    'Other (Custom)'
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form Section */}
        <div>
          <RealisticTemplateSelector 
            selectedTemplate={selectedTemplate}
            onSelectTemplate={handleTemplateSelect}
          />
          
          <RealisticCustomization
            customization={customization}
            onCustomizationChange={handleCustomizationChange}
            templateType={selectedTemplate}
            onHospitalDataChange={handleHospitalDataChange}
          />
          
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-2xl">Medical Certificate Details</CardTitle>
              <CardDescription>
                Fill in the details below. Preview updates in real-time.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={certificateType} onValueChange={setCertificateType}>
                <TabsList className="grid w-full grid-cols-4 mb-6">
                  <TabsTrigger value="prescription">🫀 Prescription</TabsTrigger>
                  <TabsTrigger value="sick-leave">🤒 Sick Leave</TabsTrigger>
                  <TabsTrigger value="fitness">💪 Fitness</TabsTrigger>
                  <TabsTrigger value="general">📄 General</TabsTrigger>
                </TabsList>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">📋 Patient & Doctor Details</h3>
                  <p className="text-sm text-muted-foreground">Fill in all required fields. Preview updates automatically.</p>

                  <div>
                    <Label htmlFor="patientName">Patient Name *</Label>
                    <Input
                      id="patientName"
                      value={formData.patientName}
                      onChange={(e) => handleInputChange('patientName', e.target.value)}
                      placeholder="Raj Chaurasiya"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="age">Age</Label>
                      <Input
                        id="age"
                        type="number"
                        value={formData.age}
                        onChange={(e) => handleInputChange('age', e.target.value)}
                        placeholder="28"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gender">Gender</Label>
                      <Select value={formData.gender} onValueChange={(value) => handleInputChange('gender', value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="diagnosis">Diagnosis *</Label>
                    <Select value={formData.diagnosis} onValueChange={(value) => handleInputChange('diagnosis', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {diagnosisOptions.map(option => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.diagnosis === 'Other (Custom)' && (
                    <div>
                      <Label htmlFor="customDiagnosis">Custom Diagnosis</Label>
                      <Input
                        id="customDiagnosis"
                        value={formData.customDiagnosis}
                        onChange={(e) => handleInputChange('customDiagnosis', e.target.value)}
                        placeholder="Enter custom diagnosis"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="leaveFrom">Leave From</Label>
                      <Input
                        id="leaveFrom"
                        type="date"
                        value={formData.leaveFrom}
                        onChange={(e) => handleInputChange('leaveFrom', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="leaveTo">Leave To</Label>
                      <Input
                        id="leaveTo"
                        type="date"
                        value={formData.leaveTo}
                        onChange={(e) => handleInputChange('leaveTo', e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="doctorName">Doctor Name *</Label>
                    <Input
                      id="doctorName"
                      value={formData.doctorName}
                      onChange={(e) => handleInputChange('doctorName', e.target.value)}
                      placeholder="Dr. Sunita Reddy"
                    />
                  </div>

                  <div>
                    <Label htmlFor="hospitalName">Hospital / Clinic Name *</Label>
                    <Input
                      id="hospitalName"
                      value={formData.hospitalName}
                      onChange={(e) => handleInputChange('hospitalName', e.target.value)}
                      placeholder="Apollo Hospital"
                    />
                  </div>

                  <div>
                    <Label htmlFor="registrationNumber">Registration Number</Label>
                    <Input
                      id="registrationNumber"
                      value={formData.registrationNumber}
                      onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                      placeholder="MCI/DL/2024/12345"
                    />
                  </div>

                  <div>
                    <Label htmlFor="dateOfIssue">Date of Issue</Label>
                    <Input
                      id="dateOfIssue"
                      type="date"
                      value={formData.dateOfIssue}
                      onChange={(e) => handleInputChange('dateOfIssue', e.target.value)}
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">Optional Notes / Instructions</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      placeholder="Additional medical notes or instructions..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button onClick={saveCertificate} className="flex-1" variant="outline">
                      💾 Save Certificate
                    </Button>
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={downloadPDF} className="flex-1">
                      Download PDF
                    </Button>
                    <Button onClick={downloadImage} className="flex-1" variant="secondary">
                      Download Image
                    </Button>
                  </div>
                </div>
              </Tabs>
            </CardContent>
          </Card>

          <CertificateHistory
            certificates={savedCertificates}
            onDelete={deleteCertificate}
            onClearAll={clearAll}
            onLoad={(cert) => {
              setCertificateType(cert.type);
              setFormData(cert.data);
              if (cert.template) setSelectedTemplate(cert.template);
              if (cert.hospitalData) setHospitalData(cert.hospitalData);
              if (cert.customization) setCustomization(cert.customization);
            }}
          />
        </div>

        {/* Preview Section */}
        <div className="lg:sticky lg:top-8 h-fit">
          <RealisticCertificatePreview
            type={certificateType}
            data={formData}
            templateType={selectedTemplate}
            customization={customization}
          />
        </div>
      </div>
    </div>
  );
};

export default CertificateGenerator;